import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { find } from "lodash";
import GravityFormForm from "gatsby-plugin-gravity-forms";

import { useGravityFormsContext } from "~/context/GravityFormsContext";

import ErrorSummary from "~/components/GravityForms/ErrorSummary";

import { labelStyles } from "~/styles/gravity-forms/label";
import { inputStyles, textareaStyles } from "~/styles/gravity-forms/input";
import { checkboxStyles } from "~/styles/gravity-forms/checkbox";
import { radioStyles } from "~/styles/gravity-forms/radio";
import { selectStyles } from "~/styles/gravity-forms/select";
import { buttonStyles } from "~/styles/gravity-forms/button";
import { h2, h3, h4 } from "~/styles/typographyStyles";
import { setElementSpacing } from "~/styles/helpers";
import transition from "~/utilities/transition";
import theme from "~/theme";

import {
    getPresetFormValues,
    prepareFormData,
    addHiddenValuesToPresets,
} from "~/utilities/formHelpers";

const FormWrapper = styled.div`
    .gravityform {
        position: relative;
    }

    .gform_fields {
        display: flex;
        flex-wrap: wrap;
        padding: 0;
        list-style-type: none;
    }

    .gform_heading {
        @media (min-width: ${theme.breakpoints.tabletPortrait}) {
            padding-bottom: ${theme.spacing.smallSpacingDesktop};
        }

        @media (min-width: ${theme.breakpoints.largeDesktop}) {
            padding-bottom: ${theme.spacing.smallSpacingWidescreen};
        }
    }

    .gfield {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
        flex: 0 1 auto;
        width: 100%;
        padding: ${({ isNewsletter }) =>
            isNewsletter
                ? `0`
                : `${theme.spacing.smallSpacingMobile}
        ${theme.spacing.smallSpacingMobile} 0`};

        @media (min-width: ${theme.breakpoints.tabletPortrait}) {
            padding: ${({ isNewsletter }) =>
                isNewsletter
                    ? `0`
                    : `${theme.spacing.smallSpacingDesktop}
        ${theme.spacing.smallSpacingDesktop} 0`};
        }

        @media (min-width: ${theme.breakpoints.largeDesktop}) {
            padding: ${({ isNewsletter }) =>
                isNewsletter
                    ? `0`
                    : `${theme.spacing.smallSpacingWidescreen}
        ${theme.spacing.smallSpacingWidescreen} 0`};
        }

        &.gfield_half {
            @media (min-width: ${theme.breakpoints.tabletPortrait}) {
                width: 50%;
            }
        }
        &.gfield_third {
            @media (min-width: ${theme.breakpoints.tabletPortrait}) {
                width: 33%;
            }
        }
        &.gfield_quarter {
            @media (min-width: ${theme.breakpoints.tabletPortrait}) {
                width: 25%;
            }
        }

        .gravityform__error_message {
            color: ${theme.palette.red};
            font-weight: 900;
            font-size: 1.6rem;
        }

        .gravityform__label {
            ${({ darkBackground }) => labelStyles({ theme, darkBackground })}
        }

        .gravityform__field__input {
            ${({ darkBackground, isNewsletter }) =>
                inputStyles({ theme, darkBackground, isNewsletter })}
        }

        .gravityform__field__input[readonly] {
            cursor: not-allowed;
            background-color: ${theme.palette.mediumGray};
            opacity: 0.6;
        }

        &.gravityform__field__hidden {
            padding: 0;

            @media (min-width: ${theme.breakpoints.tabletPortrait}) {
                padding: 0;
            }

            @media (min-width: ${theme.breakpoints.largeDesktop}) {
                padding: 0;
            }
        }

        &.hide_if_case {
            display: ${({ hidePresets }) => (hidePresets ? `none` : `flex`)};
        }

        .gravityform__field__input__textarea {
            ${textareaStyles({ theme })}
        }

        .ginput_container_html {
            h2 {
                ${h2({ theme })};
            }

            h3 {
                ${h3({ theme })};
            }

            h4 {
                ${h4({ theme })};
            }

            a {
                ${transition({}, "color")};
                color: ${theme.palette.darkBlue};

                &:hover,
                &:focus {
                    color: ${theme.palette.lightBlue};
                }
            }

            ul {
                list-style-type: disc;
                list-style-position: inside;
            }

            hr {
                ${setElementSpacing(theme, "margin-top")};
                ${setElementSpacing(theme, "margin-bottom")};
                display: block;
                border: 0;
                border-top: 1px solid ${theme.palette.darkGray};
            }
        }
    }

    .gravityform__field__checkbox {
        margin-bottom: ${({ isNewsletter }) => (isNewsletter ? `25px` : `0`)};
        ${({ darkBackground }) => checkboxStyles({ theme, darkBackground })}
    }

    .gravityform__field__radio {
        margin-bottom: ${({ isNewsletter }) => (isNewsletter ? `25px` : `0`)};
        ${({ darkBackground }) => radioStyles({ theme, darkBackground })}
    }

    .gravityform__field__select {
        ${({ darkBackground }) => selectStyles({ theme, darkBackground })}
    }

    .gravityform__button {
        ${({ isNewsletter }) => buttonStyles({ theme, isNewsletter })}
    }

    .gform_heading {
        display: none;
    }

    .gform_footer {
        display: flex;
        justify-content: center;
        padding: ${({ isNewsletter }) =>
            isNewsletter
                ? `0`
                : `${theme.spacing.smallSpacingMobile}
        ${theme.spacing.smallSpacingMobile} 0`};

        @media (min-width: ${theme.breakpoints.tabletPortrait}) {
            padding: ${({ isNewsletter }) =>
                isNewsletter
                    ? `0`
                    : `${theme.spacing.smallSpacingDesktop}
        ${theme.spacing.smallSpacingDesktop} 0`};
        }

        @media (min-width: ${theme.breakpoints.largeDesktop}) {
            padding: ${({ isNewsletter }) =>
                isNewsletter
                    ? `0`
                    : `${theme.spacing.smallSpacingWidescreen}
        ${theme.spacing.smallSpacingWidescreen} 0`};
        }
    }

    .gform_confirmation_message {
        color: ${theme.palette.yellow};
        font-weight: 900;
        font-size: 1.6rem;
        margin-bottom: 25px;
    }
`;

const GravityForm = ({
    id,
    darkBackground = false,
    presetValues = {},
    onSubmitSuccess = null,
    onSubmitError = null,
    hiddenPresets = [],
    hidePresets = false,
    isRma = false,
    isNewsletter = false,
}) => {
    const { forms, gfSettings } = useGravityFormsContext();
    const [errors, setErrors] = useState(null);
    const onSuccess = ({ confirmation, data: formData, reset }) => {
        if (onSubmitSuccess) {
            onSubmitSuccess(confirmation, formData, reset);
        }
    };
    const onError = ({ data: formData, error, reset }) => {
        console.error("onError", error);
        if (onSubmitError) {
            onSubmitError(formData, error, reset);
        }
    };
    const onValidationError = (errors) => {
        console.log("onValidationError", errors);
        setErrors(errors);
    };

    const data = find(forms, ["databaseId", parseInt(id)]);

    const updatedPresetValues =
        hiddenPresets?.length && data
            ? addHiddenValuesToPresets(data, presetValues, hiddenPresets, isRma)
            : presetValues;

    const formattedPresetValues =
        data && updatedPresetValues
            ? getPresetFormValues(data, updatedPresetValues)
            : updatedPresetValues;

    const formattedData =
        data && updatedPresetValues
            ? prepareFormData(data, updatedPresetValues)
            : data;

    return data ? (
        <FormWrapper {...{ darkBackground, hidePresets, isNewsletter }}>
            {errors && !isNewsletter ? <ErrorSummary {...{ errors }} /> : null}
            <GravityFormForm
                data={{
                    wpGfForm: formattedData,
                    wp: gfSettings,
                }}
                presetValues={formattedPresetValues}
                successCallback={onSuccess}
                errorCallback={onError}
                validationCallback={onValidationError}
            />
        </FormWrapper>
    ) : null;
};

GravityForm.propTypes = {
    id: PropTypes.string.isRequired,
    darkBackground: PropTypes.bool,
    presetValues: PropTypes.shape({}),
    onSubmitSuccess: PropTypes.func,
    onSubmitError: PropTypes.func,
    hiddenPresets: PropTypes.arrayOf(PropTypes.shape({})),
    hidePresets: PropTypes.bool,
    isRma: PropTypes.bool,
    isNewsletter: PropTypes.bool,
};

export default GravityForm;
